<script setup lang="ts">
import {
  GamesApiError,
  LookupGameClaim,
  RedeemGameClaim,
} from "@/api/gamesApi";
import GamesDemoAlert from "@/components/games/GamesDemoAlert.vue";
import { useGamesFormat } from "@/composables/useGamesFormat";
import { useAuthStore } from "@/stores/auth.store";
import {
  GAME_TYPE_ICONS,
  PLAY_STATUS_COLORS,
  PRIZE_TYPE_ICONS,
  normalizeClaimCode,
  type GameClaim,
} from "@/type/games";

/**
 * Reception / till: the customer shows the code from the kiosk, staff look it up,
 * hand over or apply the prize, and mark it given — once. The full phone number is
 * shown here (and only here) so staff can check it is the customer's own.
 *
 * ⚠️ Marking a code as given does not apply the discount by itself. The POS applies and
 * redeems a code inside the sale (docs/backend/pos-game-prizes.md); this page stays for
 * prizes handed over outside a till sale, and says what to do for each prize type.
 */

definePage({ meta: { permissionKey: "page.games.redeem" } });

const { t } = useI18n({ useScope: "global" });
const auth = useAuthStore();
const {
  prizeTitle,
  prizeValue,
  prizeTypeLabel,
  playStatusLabel,
  formatDate,
  formatDateTime,
  formatInteger,
} = useGamesFormat();

const code = ref("");
const claim = ref<GameClaim | null>(null);
const error = ref<string | null>(null);
const lookingUp = ref(false);
const redeeming = ref(false);
const justRedeemed = ref(false);
/** Wrapper element: AppTextField does not expose focus(), so the input is found inside it. */
const codeWrap = ref<HTMLElement | null>(null);

const normalized = computed(() => normalizeClaimCode(code.value));
const codeComplete = computed(() => /^[A-Z0-9]{3}-[A-Z0-9]{3}$/.test(normalized.value));

function messageFor(err: unknown) {
  if (err instanceof GamesApiError)
    return t(`alkokh.games.errors.${err.code}`);

  return (err as Error)?.message || t("alkokh.games.messages.loadFailed");
}

async function lookup() {
  if (!codeComplete.value)
    return;
  error.value = null;
  justRedeemed.value = false;
  try {
    claim.value = await LookupGameClaim(lookingUp, normalized.value);
  }
  catch (err) {
    claim.value = null;
    error.value = messageFor(err);
  }
}

async function redeem() {
  if (!claim.value)
    return;
  try {
    claim.value = await RedeemGameClaim(redeeming, claim.value.play.claim_code, auth.username ?? "");
    justRedeemed.value = true;
  }
  catch (err) {
    // Refresh the card (e.g. it was redeemed at another till meanwhile), THEN show why —
    // lookup() clears the error when it starts, so the order matters.
    const reason = messageFor(err);
    await lookup();
    error.value = reason;
  }
}

function next() {
  code.value = "";
  claim.value = null;
  error.value = null;
  justRedeemed.value = false;
  nextTick(() => codeWrap.value?.querySelector("input")?.focus());
}

/** What staff physically do for this prize. */
const tillInstruction = computed(() => {
  const prize = claim.value?.prize;
  if (!prize)
    return "";
  const target = prize.item_label || t("alkokh.games.redeem.wholeInvoice");
  switch (prize.prize_type) {
    case "percent_discount":
      return t("alkokh.games.redeem.doPercent", { value: prizeValue(prize), target });
    case "fixed_discount":
      return t("alkokh.games.redeem.doFixed", { value: prizeValue(prize) });
    case "free_item":
      return t("alkokh.games.redeem.doFree", { item: prizeValue(prize) });
    case "loyalty_points":
      return t("alkokh.games.redeem.doPoints", { points: formatInteger(prize.value) });
  }
});

const localPhone = computed(() => claim.value?.play.phone.replace(/^\+964/, "0") ?? "");
</script>

<template>
  <div class="redeem-page">
    <div class="mb-4">
      <h1 class="text-h4">
        {{ t("alkokh.games.redeem.title") }}
      </h1>
      <p class="text-body-2 text-medium-emphasis mb-0">
        {{ t("alkokh.games.redeem.subtitle") }}
      </p>
    </div>

    <GamesDemoAlert />

    <VCard
      elevation="0"
      border
      class="mb-4"
    >
      <VCardText>
        <form
          ref="codeWrap"
          class="redeem-page__form"
          @submit.prevent="lookup"
        >
          <AppTextField
            v-model="code"
            class="redeem-page__code"
            dir="ltr"
            autofocus
            autocomplete="off"
            :label="t('alkokh.games.redeem.codeLabel')"
            placeholder="ABC-123"
            maxlength="8"
            prepend-inner-icon="tabler-ticket"
            @update:model-value="claim = null; error = null"
          />
          <VBtn
            type="submit"
            color="primary"
            size="large"
            prepend-icon="tabler-search"
            :loading="lookingUp"
            :disabled="!codeComplete"
          >
            {{ t("alkokh.games.redeem.lookup") }}
          </VBtn>
        </form>
      </VCardText>
    </VCard>

    <VAlert
      v-if="error"
      type="error"
      variant="tonal"
      class="mb-4"
    >
      {{ error }}
    </VAlert>

    <VCard
      v-if="claim"
      elevation="0"
      border
      class="redeem-page__result"
      :style="{ borderInlineStartColor: claim.prize?.color || 'rgb(var(--v-theme-primary))' }"
    >
      <VCardText>
        <VAlert
          v-if="justRedeemed"
          type="success"
          variant="tonal"
          class="mb-4"
          icon="tabler-circle-check"
        >
          {{ t("alkokh.games.redeem.success") }}
        </VAlert>

        <div class="redeem-page__prize">
          <VAvatar
            size="56"
            :color="claim.prize?.color || 'primary'"
            variant="flat"
            rounded="lg"
          >
            <VIcon
              :icon="claim.prize ? PRIZE_TYPE_ICONS[claim.prize.prize_type] : 'tabler-gift'"
              size="28"
              color="white"
            />
          </VAvatar>
          <div>
            <div class="text-h5 font-weight-bold">
              {{ claim.prize ? prizeTitle(claim.prize) : claim.play.prize_title }}
            </div>
            <div
              v-if="claim.prize"
              class="text-body-1"
            >
              {{ prizeTypeLabel(claim.prize.prize_type) }} · {{ prizeValue(claim.prize) }}
            </div>
          </div>
          <VChip
            :color="PLAY_STATUS_COLORS[claim.play.status]"
            label
            class="ms-auto"
          >
            {{ playStatusLabel(claim.play.status) }}
          </VChip>
        </div>

        <VAlert
          v-if="claim.redeemable && tillInstruction"
          type="info"
          variant="tonal"
          density="compact"
          icon="tabler-info-circle"
          class="my-4"
        >
          {{ tillInstruction }}
        </VAlert>

        <dl class="redeem-page__facts">
          <dt>{{ t("alkokh.games.redeem.code") }}</dt>
          <dd
            dir="ltr"
            class="font-weight-bold"
          >
            {{ claim.play.claim_code }}
          </dd>
          <dt>{{ t("alkokh.games.redeem.phone") }}</dt>
          <dd dir="ltr">
            {{ localPhone }}
          </dd>
          <dt>{{ t("alkokh.games.redeem.game") }}</dt>
          <dd>
            <VIcon
              :icon="GAME_TYPE_ICONS[claim.play.game_type]"
              size="16"
              class="me-1"
            />
            {{ claim.play.game_title }}
            <VChip
              v-if="!claim.play.completed"
              size="x-small"
              label
              class="ms-1"
            >
              {{ t("alkokh.games.plays.consolation") }}
            </VChip>
          </dd>
          <dt>{{ t("alkokh.games.redeem.playedAt") }}</dt>
          <dd>{{ formatDateTime(claim.play.played_at) }}</dd>
          <dt>{{ t("alkokh.games.redeem.expiresOn") }}</dt>
          <dd>{{ formatDate(claim.play.expires_on) }}</dd>
          <template v-if="claim.play.redeemed_at">
            <dt>{{ t("alkokh.games.redeem.redeemedAt") }}</dt>
            <dd>
              {{ formatDateTime(claim.play.redeemed_at) }}
              <span
                v-if="claim.play.redeemed_by"
                class="text-medium-emphasis"
              > · {{ claim.play.redeemed_by }}</span>
            </dd>
          </template>
          <!-- Redeemed at the till: the sale that used it (docs/backend/pos-game-prizes.md). -->
          <template v-if="claim.play.sales_invoice">
            <dt>{{ t("alkokh.games.redeem.posSale") }}</dt>
            <dd dir="ltr">
              <RouterLink
                :to="{ name: 'accounting-sales-invoices-id', params: { id: claim.play.sales_invoice } }"
                class="text-link"
              >
                {{ claim.play.sales_invoice }}
              </RouterLink>
            </dd>
          </template>
          <template v-if="claim.prize?.notes">
            <dt>{{ t("alkokh.games.prizeFields.notes") }}</dt>
            <dd>{{ claim.prize.notes }}</dd>
          </template>
        </dl>

        <div class="redeem-page__actions">
          <VBtn
            variant="text"
            @click="next"
          >
            {{ t("alkokh.games.redeem.next") }}
          </VBtn>
          <VBtn
            v-if="claim.redeemable"
            color="success"
            size="large"
            prepend-icon="tabler-check"
            :loading="redeeming"
            @click="redeem"
          >
            {{ t("alkokh.games.redeem.markGiven") }}
          </VBtn>
        </div>
      </VCardText>
    </VCard>
  </div>
</template>

<style scoped>
.redeem-page {
  max-inline-size: 760px;
}

.redeem-page__form {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-end;
  gap: 12px;
}

.redeem-page__code {
  flex: 1 1 220px;
}

.redeem-page__code :deep(input) {
  font-size: 1.4rem;
  font-weight: 700;
  letter-spacing: 0.12em;
  text-transform: uppercase;
}

.redeem-page__result {
  border-inline-start-width: 6px !important;
}

.redeem-page__prize {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 16px;
}

.redeem-page__facts {
  display: grid;
  grid-template-columns: minmax(120px, max-content) 1fr;
  gap: 8px 20px;
  margin: 16px 0 0;
}

.redeem-page__facts dt {
  color: rgba(var(--v-theme-on-surface), 0.6);
}

.redeem-page__facts dd {
  margin: 0;
}

.redeem-page__actions {
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: 8px;
  margin-block-start: 20px;
}
</style>
